/**
 * GST helper utilities (India) — CGST + SGST split (equal halves for intra-state)
 */

export const GST_RATES = [0, 5, 12, 18, 28];

/** Match a stored rate like "18.000" to a dropdown option value. */
export function gstRateNumber(rate) {
  const n = parseFloat(rate);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function gstRateSelectValue(rate, fallback = '0') {
  if (rate == null || rate === '') return String(fallback);
  const n = Number(rate);
  if (!Number.isFinite(n)) return String(fallback);
  const match = GST_RATES.find((r) => Math.abs(r - n) < 0.001);
  return String(match != null ? match : n);
}

const round3 = (n) => Math.round((parseFloat(n) || 0) * 1000) / 1000;

/** Split total GST into CGST + SGST (50/50) */
export function splitCgstSgst(gstAmount, gstRate) {
  const gst = round3(gstAmount);
  const rate = parseFloat(gstRate) || 0;
  const halfRate = round3(rate / 2);
  const cgst = round3(gst / 2);
  const sgst = round3(gst - cgst); // remainder avoids 0.001 drift
  return { cgst, sgst, cgstRate: halfRate, sgstRate: halfRate, gst };
}

/** Split an inclusive amount into base + GST */
export function splitInclusive(amount, gstRate) {
  const total = parseFloat(amount) || 0;
  const rate = parseFloat(gstRate) || 0;
  if (rate <= 0) {
    return { base: total, gst: 0, total, ...splitCgstSgst(0, 0) };
  }
  const base = total / (1 + rate / 100);
  const gst = total - base;
  return { base, gst, total, ...splitCgstSgst(gst, rate) };
}

/** Build exclusive amount → base + GST + total */
export function splitExclusive(amount, gstRate) {
  const base = parseFloat(amount) || 0;
  const rate = parseFloat(gstRate) || 0;
  const gst = base * (rate / 100);
  return { base, gst, total: base + gst, ...splitCgstSgst(gst, rate) };
}

/** From entered price + mode, return breakdown */
export function priceBreakdown(amount, gstRate, gstInclusive) {
  if (gstInclusive) {
    return splitInclusive(amount, gstRate);
  }
  return splitExclusive(amount, gstRate);
}

/**
 * Line total for invoice item.
 * Returns { taxable, gst, cgst, sgst, cgstRate, sgstRate, lineTotal }
 */
export function lineGstBreakdown(quantity, unitPrice, discount, gstRate, gstInclusive) {
  const qty = parseFloat(quantity) || 0;
  const price = parseFloat(unitPrice) || 0;
  const disc = parseFloat(discount) || 0;
  const lineAmount = qty * price - disc;
  const rate = parseFloat(gstRate) || 0;

  if (rate <= 0) {
    return { taxable: lineAmount, gst: 0, cgst: 0, sgst: 0, cgstRate: 0, sgstRate: 0, lineTotal: lineAmount };
  }

  const parts = gstInclusive
    ? splitInclusive(lineAmount, rate)
    : splitExclusive(lineAmount, rate);

  return {
    taxable: parts.base,
    gst: parts.gst,
    cgst: parts.cgst,
    sgst: parts.sgst,
    cgstRate: parts.cgstRate,
    sgstRate: parts.sgstRate,
    lineTotal: parts.total
  };
}
